import React, { useState, useCallback, useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableTask from './sortableTask';
import { Tasks } from 'common/src/utils/types';
import { getInitials, formatDate } from 'common/src/utils/functions';

interface TaskColumnProps {
  title: string;
  tasks: Tasks[];
  onTaskClick: (task: Tasks) => void;
  onEdit: (task: Tasks) => void;
  onDelete: (task: Tasks) => void;
  onAddComment: (task: Tasks) => void;
}

const MemoizedSortableTask = React.memo(SortableTask);

const TaskColumn: React.FC<TaskColumnProps> = React.memo(
  ({ title, tasks, onTaskClick, onEdit, onDelete, onAddComment }) => {
    const { setNodeRef } = useDroppable({
      id: title.toLowerCase().replace(' ', '-'),
    });
    const [visibleTasks, setVisibleTasks] = useState(10);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    const toggleSortOrder = useCallback(() => {
      setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
    }, []);

    const sortedTasks = useMemo(() => {
      return [...tasks].sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }, [tasks, sortOrder]);

    const loadMoreTasks = useCallback(() => {
      setVisibleTasks((prev) => prev + 10);
    }, []);

    const getColumnColor = useCallback((title: string) => {
      switch (title.toLowerCase()) {
        case 'pending':
          return 'bg-yellow-50';
        case 'accepted':
          return 'bg-blue-50';
        case 'assigned':
          return 'bg-purple-50';
        case 'progress':
          return 'bg-orange-50';
        case 'complete':
          return 'bg-green-50';
        case 'rejected':
          return 'bg-red-50';
        default:
          return 'bg-gray-50';
      }
    }, []);

    const columnId = useMemo(
      () => title.toLowerCase().replace(' ', '-'),
      [title]
    );

    return (
      <SortableContext
        id={columnId}
        items={tasks.map((task) => `${task.status}-${task.id}`)}
        strategy={verticalListSortingStrategy}
      >
        <div
          className={`w-full h-full flex flex-col overflow-y-auto p-4 rounded-lg mr-4 ${getColumnColor(
            title
          )}`}
          id={columnId}
        >
          <div className='flex justify-between items-center mb-4'>
            <h2 className='text-xl font-bold'>{title}</h2>
            <button
              onClick={toggleSortOrder}
              className='text-gray-600 hover:text-gray-800'
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          <div ref={setNodeRef}>
            {sortedTasks.slice(0, visibleTasks).map((task) => {
              const formattedDate = formatDate(task.created_at);

              return (
                <div key={task.id} className='mb-4'>
                  <MemoizedSortableTask
                    id={`${task.status}-${task.id}-colum`}
                    cardTitle={task.category}
                    cardCategory={task.description}
                    onClick={() => onTaskClick(task)}
                    name={task.name}
                    last_name={task.last_name}
                    assignedUserInitials={getInitials(
                      task.User?.first_name,
                      task.User?.last_name
                    )}
                    assignedUserName={
                      task.User?.first_name && task.User?.last_name
                        ? `${task.User?.first_name} ${task.User?.last_name}`
                        : 'No Asignado'
                    }
                    onEdit={() => onEdit(task)}
                    price={task.price}
                    date={formattedDate}
                    phone_number={task.phone_number}
                    onDelete={() => onDelete(task)}
                    onAddComment={() => onAddComment(task)}
                    task={task}
                  />
                </div>
              );
            })}
          </div>
          {visibleTasks < tasks.length && (
            <button
              className='w-full py-2 mt-4 bg-purple-500 text-white border-none rounded cursor-pointer hover:bg-purple-600 transition-colors duration-300'
              onClick={loadMoreTasks}
            >
              Ver más
            </button>
          )}
        </div>
      </SortableContext>
    );
  }
);

export default TaskColumn;